import { ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { GuestListAttributes, ImportGuestListMutationInput, Mutation, Query, SaveGuestListMutationInput } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as GuestList from './GuestList';
import * as GuestListImport from './GuestListImport';
import { GUEST_LIST_FIELDS, GUEST_LIST_IMPORT_FIELDS } from './fragments';

/** Guest lists on AppSchema; nothing here catches or evicts, for the reasons `App/Presale/Api.ts` gives. */

// The event's own `id` is selected so the result normalizes onto the `Event` entity `EVENT_DETAIL_QUERY` wrote.
export const EVENT_GUEST_LISTS_QUERY = gql`
  query EventGuestLists($eventId: Int!) {
    event(id: $eventId) {
      id
      guestLists {
        ...GuestListFields
      }
    }
  }
  ${GUEST_LIST_FIELDS}
`;

export const SAVE_GUEST_LIST_MUTATION = gql`
  mutation saveGuestList($input: SaveGuestListMutationInput!) {
    saveGuestList(input: $input) {
      guestList {
        ...GuestListFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${GUEST_LIST_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const IMPORT_GUEST_LIST_MUTATION = gql`
  mutation importGuestList($input: ImportGuestListMutationInput!) {
    importGuestList(input: $input) {
      eventExport {
        ...GuestListImportFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${GUEST_LIST_IMPORT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** `network-only`: the Tickets tab re-reads after a save, which a cached list would answer with the old state. */
export const fetchGuestLists = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<GuestList.GuestList[]> => {
  const { data } = await client.query<Query>({
    query: EVENT_GUEST_LISTS_QUERY,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  if (!data.event) {
    throw new Error('Failed to fetch guest lists.');
  }

  return data.event.guestLists.map(GuestList.fromGql);
};

/** Creates when the attributes carry no `id`, updates when they do; the caller serializes through `GuestListInput.toGqlInput`. */
export const saveGuestList = async (
  client: ApolloClient<NormalizedCacheObject>,
  guestList: GuestListAttributes,
): Promise<VPResult<GuestList.GuestList, VPErrors.VPError[]>> => {
  const input: SaveGuestListMutationInput = { guestList };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_GUEST_LIST_MUTATION,
    variables: { input },
  });

  const payload = data?.saveGuestList;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.guestList) {
    return { success: false, errors: [{ path: '', message: 'Failed to save the guest list.' }] };
  }

  return { success: true, data: GuestList.fromGql(payload.guestList) };
};

/** Starts a background import of CSV text into a list; the record comes back pending and is polled through `fetchGuestLists`. */
export const importGuestList = async (
  client: ApolloClient<NormalizedCacheObject>,
  guestListId: number,
  csv: string,
): Promise<VPResult<GuestListImport.GuestListImport, VPErrors.VPError[]>> => {
  const input: ImportGuestListMutationInput = { guestListId, csv };

  const { data } = await client.mutate<Mutation>({
    mutation: IMPORT_GUEST_LIST_MUTATION,
    variables: { input },
  });

  const payload = data?.importGuestList;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // "Already running" answers with the running record and an error; the error is the outcome.
  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.eventExport) {
    return { success: false, errors: [{ path: '', message: 'Failed to start the import.' }] };
  }

  return { success: true, data: GuestListImport.fromGql(payload.eventExport) };
};
